import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../App";
import { getApiPrefix } from '../utils/apiHelper';
import { handleApiError } from "../utils/errorHandler";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { 
  TrendingUp, TrendingDown, Calendar, Euro, AlertTriangle, 
  CheckCircle2, Clock, ArrowUpRight, ArrowDownRight, Wallet,
  ChevronLeft, ChevronRight, FileText, Building, Car, Loader2,
  Brain, Sparkles, RefreshCw
} from "lucide-react";
import { PageLoader } from "../components/ui/page-loader";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  AreaChart, Area, Legend, ReferenceLine
} from "recharts";
import { useTranslation } from "../contexts/I18nContext";

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

export default function CashFlowDashboardPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [scadenze, setScadenze] = useState([]);
  const [fatture, setFatture] = useState([]);
  const [affitti, setAffitti] = useState([]);
  const [scadenzeProgrammate, setScadenzeProgrammate] = useState([]);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [saldoTesoreria, setSaldoTesoreria] = useState({ banche: 0, carte: 0, totale: 0 });
  
  // Selezione movimenti per filtro interattivo
  const [selectedMovimenti, setSelectedMovimenti] = useState(new Set());
  const [showAllMovimenti, setShowAllMovimenti] = useState(true);
  
  // ML Prediction
  const [showMLPrediction, setShowMLPrediction] = useState(false);
  const [mlPredictions, setMlPredictions] = useState([]);
  const [calculatingML, setCalculatingML] = useState(false);
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const prefix = getApiPrefix();
      const [fattRes, affRes, schedRes, scadRes, riepilogoRes] = await Promise.all([
        api.get(`${prefix}/invoices`),
        api.get(`${prefix}/reports/affitti-previsti`).catch(() => ({ data: { affitti: [] } })),
        api.get(`${prefix}/scheduled-payments`),
        api.get(`${prefix}/reports/scadenziario`).catch(() => ({ data: { scadenze: [] } })),
        api.get(`${prefix}/tesoreria/riepilogo-saldi`).catch(() => ({ data: { totale_banche: 0, totale_carte: 0, disponibilita_totale: 0 } }))
      ]);
      
      setFatture(fattRes.data || []);
      setAffitti(affRes.data?.affitti || []);
      setScadenzeProgrammate(schedRes.data || []);
      setScadenze(scadRes.data?.scadenze || []);
      setSaldoTesoreria({
        banche: riepilogoRes.data?.totale_banche || 0,
        carte: riepilogoRes.data?.totale_carte || 0,
        totale: riepilogoRes.data?.totale_disponibilita || 0
      });
    } catch (err) {
      handleApiError(err, t("errors.loadCashFlow"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Parse date helper
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        } else {
          return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        }
      }
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.warn('Date parsing error:', e);
    }
    return null;
  };

  // Costruisci lista movimenti dettagliati
  const movimentiDettaglio = useMemo(() => {
    const items = [];
    
    // Scadenze fatture (uscite)
    scadenze.forEach((s, idx) => {
      const date = parseDate(s.data_scadenza);
      if (date && date.getFullYear() === currentYear && s.stato !== 'pagato') {
        items.push({
          id: `fattura-${s.id}-${idx}`,
          tipo: 'fattura',
          descrizione: s.descrizione || `Fattura ${s.numero_fattura || ''}`,
          importo: s.importo || 0,
          data: s.data_scadenza,
          mese: date.getMonth(),
          flusso: 'uscita',
          beneficiario: s.fornitore || s.ragione_sociale || ''
        });
      }
    });
    
    // Scadenze programmate
    scadenzeProgrammate.forEach(s => {
      const date = parseDate(s.data_scadenza);
      if (date && date.getFullYear() === currentYear && s.stato !== 'pagato') {
        items.push({
          id: `prog-${s.id}`,
          tipo: s.tipo || 'programmata',
          descrizione: s.descrizione,
          importo: s.importo || 0,
          data: s.data_scadenza,
          mese: date.getMonth(),
          flusso: s.tipo_flusso || 'uscita',
          beneficiario: s.beneficiario || ''
        });
      }
    });
    
    // Affitti previsti (entrate)
    affitti.forEach((a, idx) => {
      if (a.mese_anno) {
        const [mese, anno] = a.mese_anno.split('/').map(Number);
        if (anno === currentYear && mese >= 1 && mese <= 12) {
          items.push({
            id: `affitto-${idx}`,
            tipo: 'affitto',
            descrizione: `Affitto ${a.appartamento || ''}`,
            importo: a.importo_previsto || 0,
            data: `01-${String(mese).padStart(2, '0')}-${anno}`,
            mese: mese - 1,
            flusso: 'entrata',
            beneficiario: a.inquilino || ''
          });
        }
      }
    });
    
    // Ordina per data
    items.sort((a, b) => {
      const dateA = parseDate(a.data);
      const dateB = parseDate(b.data);
      return (dateA || new Date(0)) - (dateB || new Date(0));
    });
    
    return items;
  }, [scadenze, scadenzeProgrammate, affitti, currentYear]);

  // Filtra movimenti in base alla selezione
  const filteredMovimenti = useMemo(() => {
    if (showAllMovimenti || selectedMovimenti.size === 0) {
      return movimentiDettaglio;
    }
    return movimentiDettaglio.filter(m => selectedMovimenti.has(m.id));
  }, [movimentiDettaglio, selectedMovimenti, showAllMovimenti]);

  // Calcola dati mensili basati sui movimenti filtrati
  const getMonthlyData = useCallback(() => {
    const data = MONTHS.map((month, idx) => ({
      name: month,
      month: idx,
      uscite: 0,
      entrate: 0,
      saldo: 0
    }));

    filteredMovimenti.forEach(m => {
      if (m.flusso === 'entrata') {
        data[m.mese].entrate += m.importo;
      } else {
        data[m.mese].uscite += m.importo;
      }
    });

    // Calcola saldo per mese
    data.forEach(d => {
      d.saldo = d.entrate - d.uscite;
    });

    return data;
  }, [filteredMovimenti]);

  const monthlyData = getMonthlyData();
  const today = new Date();
  const currentMonth = today.getMonth();

  // Calcola totali
  const totals = useMemo(() => {
    const uscite = filteredMovimenti.filter(m => m.flusso === 'uscita');
    const entrate = filteredMovimenti.filter(m => m.flusso === 'entrata');
    
    return {
      totaleUscite: uscite.reduce((sum, m) => sum + m.importo, 0),
      totaleEntrate: entrate.reduce((sum, m) => sum + m.importo, 0),
      countUscite: uscite.length,
      countEntrate: entrate.length
    };
  }, [filteredMovimenti]);

  // Toggle selezione movimento
  const toggleMovimento = (id) => {
    const newSet = new Set(selectedMovimenti);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedMovimenti(newSet);
    setShowAllMovimenti(false);
  };

  // Seleziona tutti di un tipo
  const selectByType = (tipo) => {
    const ids = movimentiDettaglio.filter(m => m.tipo === tipo).map(m => m.id);
    const newSet = new Set(selectedMovimenti);
    ids.forEach(id => newSet.add(id));
    setSelectedMovimenti(newSet);
    setShowAllMovimenti(false);
  };

  // Deseleziona tutti
  const clearSelection = () => {
    setSelectedMovimenti(new Set());
    setShowAllMovimenti(true);
  };

  // ML Prediction - Simple Linear Regression based on historical data
  const calculateMLPrediction = () => {
    setCalculatingML(true);
    
    // Simula calcolo ML (in produzione sarebbe un endpoint backend)
    setTimeout(() => {
      const predictions = [];
      
      // Usa i dati storici per calcolare trend
      const historicalSaldos = monthlyData.slice(0, currentMonth + 1).map(d => d.saldo);
      
      // Calcola media e trend
      const avgSaldo = historicalSaldos.length > 0 
        ? historicalSaldos.reduce((a, b) => a + b, 0) / historicalSaldos.length 
        : 0;
      
      // Calcola trend (pendenza)
      let trend = 0;
      if (historicalSaldos.length > 1) {
        const n = historicalSaldos.length;
        const sumX = (n * (n - 1)) / 2;
        const sumY = historicalSaldos.reduce((a, b) => a + b, 0);
        const sumXY = historicalSaldos.reduce((sum, y, x) => sum + x * y, 0);
        const sumX2 = (n * (n - 1) * (2 * n - 1)) / 6;
        trend = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX) || 0;
      }
      
      // Genera previsioni per i prossimi 12 mesi
      let cumulativeSaldo = monthlyData.reduce((sum, d) => sum + d.saldo, 0);
      
      for (let i = 0; i < 12; i++) {
        const monthIdx = (currentMonth + 1 + i) % 12;
        const yearOffset = Math.floor((currentMonth + 1 + i) / 12);
        const predictedSaldo = avgSaldo + trend * (historicalSaldos.length + i);
        
        // Aggiungi variabilità stagionale basata sui dati esistenti
        const seasonalFactor = monthlyData[monthIdx].saldo !== 0 
          ? monthlyData[monthIdx].saldo / (Math.abs(avgSaldo) || 1) * 0.3
          : 0;
        
        const adjustedSaldo = predictedSaldo * (1 + seasonalFactor);
        cumulativeSaldo += adjustedSaldo;
        
        predictions.push({
          name: `${MONTHS[monthIdx]} ${currentYear + yearOffset}`,
          saldoPrevisto: Math.round(adjustedSaldo),
          cumulativo: Math.round(cumulativeSaldo),
          confidence: Math.max(0.5, 0.95 - i * 0.03) // Confidenza decresce nel tempo
        });
      }
      
      setMlPredictions(predictions);
      setCalculatingML(false);
    }, 1500);
  };

  useEffect(() => {
    if (showMLPrediction && mlPredictions.length === 0) {
      calculateMLPrediction();
    }
  }, [showMLPrediction]);

  const formatCurrency = (val) =>
    new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(val || 0);

  const formatDate = (dateStr) => {
    const date = parseDate(dateStr);
    if (!date) return dateStr;
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  };

  // Dati per grafico con saldo cumulativo
  const chartDataWithCumulative = useMemo(() => {
    let cumulative = 0;
    return monthlyData.map(m => {
      cumulative += m.saldo;
      return {
        ...m,
        saldoCumulativo: cumulative
      };
    });
  }, [monthlyData]);

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cashflow-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
              <Wallet className="h-5 w-5 text-white" />
            </div>
            Cash Flow Dashboard
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Analisi interattiva crediti e debiti</p>
        </div>
        <div className="flex items-center gap-4">
          <Badge variant="outline" className="text-sm px-3 py-1 font-mono">
            Anno {currentYear}
          </Badge>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => setCurrentYear(y => y - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-semibold text-lg px-3">{currentYear}</span>
            <Button variant="outline" size="icon" onClick={() => setCurrentYear(y => y + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />{t('common.refresh')}</Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-l-4 border-l-red-500 bg-gradient-to-r from-red-50/50 to-transparent dark:from-red-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wider">Uscite Totali</p>
                <p className="text-2xl font-bold font-mono text-red-600 mt-1">{formatCurrency(totals.totaleUscite)}</p>
                <p className="text-xs text-muted-foreground mt-1">{totals.countUscite} movimenti</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <ArrowDownRight className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500 bg-gradient-to-r from-emerald-50/50 to-transparent dark:from-emerald-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Entrate Totali</p>
                <p className="text-2xl font-bold font-mono text-emerald-600 mt-1">{formatCurrency(totals.totaleEntrate)}</p>
                <p className="text-xs text-muted-foreground mt-1">{totals.countEntrate} movimenti</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <ArrowUpRight className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-indigo-500 bg-gradient-to-r from-indigo-50/50 to-transparent dark:from-indigo-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">{t('common.treasuryBalance')}</p>
                <p className={`text-2xl font-bold font-mono mt-1 ${saldoTesoreria.totale >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(saldoTesoreria.totale)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Banche: {formatCurrency(saldoTesoreria.banche)} | Carte: {formatCurrency(saldoTesoreria.carte)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                <Building className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500 bg-gradient-to-r from-blue-50/50 to-transparent dark:from-blue-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Saldo Anno</p>
                <p className={`text-2xl font-bold font-mono mt-1 ${totals.totaleEntrate - totals.totaleUscite >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {formatCurrency(totals.totaleEntrate - totals.totaleUscite)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Euro className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500 bg-gradient-to-r from-purple-50/50 to-transparent dark:from-purple-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> Previsione ML
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <Switch 
                    checked={showMLPrediction} 
                    onCheckedChange={setShowMLPrediction}
                    id="ml-switch"
                  />
                  <Label htmlFor="ml-switch" className="text-sm cursor-pointer">
                    {showMLPrediction ? 'Attiva' : 'Disattiva'}
                  </Label>
                </div>
              </div>
              <div className="h-12 w-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <Brain className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtro rapido per tipo */}
      {!showAllMovimenti && selectedMovimenti.size > 0 && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-3 flex items-center justify-between">
            <span className="text-sm font-medium text-blue-700">
              {selectedMovimenti.size} movimenti selezionati - I grafici mostrano solo i dati selezionati
            </span>
            <Button variant="outline" size="sm" onClick={clearSelection}>
              Mostra Tutti
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Grafico Andamento Mensile */}
        <Card className="shadow-sm">
          <CardHeader className="border-b bg-slate-50/50 dark:bg-slate-900/50">
            <CardTitle className="text-base font-semibold">Andamento Mensile {currentYear}</CardTitle>
            <CardDescription>Confronto entrate e uscite per mese</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" className="text-xs" />
                  <YAxis tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} className="text-xs" />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                  />
                  <Legend />
                  <Bar dataKey="entrate" name="Entrate" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="uscite" name="Uscite" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Grafico Saldo Cumulativo */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Saldo Cumulativo</CardTitle>
            <CardDescription>Evoluzione della liquidità nel tempo</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartDataWithCumulative} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" className="text-xs" />
                  <YAxis tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} className="text-xs" />
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                  />
                  <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                  <defs>
                    <linearGradient id="saldoGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <Area 
                    type="monotone" 
                    dataKey="saldoCumulativo" 
                    name="Saldo Cumulativo" 
                    stroke="#3b82f6" 
                    fill="url(#saldoGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ML Prediction Section */}
      {showMLPrediction && (
        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-500" />
              Previsione Liquidità (Machine Learning)
            </CardTitle>
            <CardDescription>
              Proiezione basata su regressione lineare con fattori stagionali
            </CardDescription>
          </CardHeader>
          <CardContent>
            {calculatingML ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
                <span className="ml-3 text-muted-foreground">Calcolo previsioni in corso...</span>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={mlPredictions} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="name" className="text-xs" />
                      <YAxis tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} className="text-xs" />
                      <Tooltip 
                        formatter={(value, name) => [formatCurrency(value), name === 'cumulativo' ? 'Liquidità Prevista' : 'Saldo Mensile']}
                        contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                      />
                      <defs>
                        <linearGradient id="predictionGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <Area 
                        type="monotone" 
                        dataKey="cumulativo" 
                        name="Liquidità Prevista" 
                        stroke="#8b5cf6" 
                        fill="url(#predictionGradient)"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                      />
                      <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-4 gap-4 text-center">
                  {mlPredictions.slice(0, 4).map((p, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-white border">
                      <div className="text-xs text-muted-foreground">{p.name}</div>
                      <div className={`text-lg font-bold ${p.cumulativo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(p.cumulativo)}
                      </div>
                      <div className="text-xs text-purple-600">
                        {Math.round(p.confidence * 100)}% confidenza
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dettaglio Movimenti - Interattivo */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">{t('common.movementDetails')}</CardTitle>
              <CardDescription>
                Seleziona le righe per filtrare i grafici (stile Power BI)
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => selectByType('fattura')}>{t('invoices.title')}</Button>
              <Button variant="outline" size="sm" onClick={() => selectByType('affitto')}>
                Affitti
              </Button>
              <Button variant="outline" size="sm" onClick={() => selectByType('programmata')}>
                Programmate
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b">
                  <th className="text-left py-2 px-2 w-8">
                    <Checkbox 
                      checked={showAllMovimenti}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          clearSelection();
                        }
                      }}
                    />
                  </th>
                  <th className="text-left py-2 px-2">{t('common.flow')}</th>
                  <th className="text-left py-2 px-2">{t("common.type")}</th>
                  <th className="text-left py-2 px-2">{t("common.description")}</th>
                  <th className="text-left py-2 px-2">{t('common.beneficiary')}</th>
                  <th className="text-right py-2 px-2">{t("common.amount")}</th>
                  <th className="text-left py-2 px-2">{t("common.date")}</th>
                </tr>
              </thead>
              <tbody>
                {movimentiDettaglio.map((m) => {
                  const isSelected = selectedMovimenti.has(m.id);
                  
                  return (
                    <tr 
                      key={m.id} 
                      className={`border-b cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-blue-50' : ''}`}
                      onClick={() => toggleMovimento(m.id)}
                    >
                      <td className="py-2 px-2">
                        <Checkbox checked={isSelected} />
                      </td>
                      <td className="py-2 px-2">
                        {m.flusso === 'entrata' ? (
                          <ArrowUpRight className="h-4 w-4 text-green-600" />
                        ) : (
                          <ArrowDownRight className="h-4 w-4 text-red-600" />
                        )}
                      </td>
                      <td className="py-2 px-2">
                        <Badge variant="outline" className="text-xs">
                          {m.tipo}
                        </Badge>
                      </td>
                      <td className="py-2 px-2 font-medium max-w-[200px]">
                        <span className="block truncate" title={m.descrizione}>{m.descrizione}</span>
                      </td>
                      <td className="py-2 px-2 text-muted-foreground max-w-[150px]">
                        <span className="block truncate" title={m.beneficiario || '-'}>{m.beneficiario || '-'}</span>
                      </td>
                      <td className={`py-2 px-2 text-right font-semibold ${m.flusso === 'entrata' ? 'text-green-600' : 'text-red-600'}`}>
                        {m.flusso === 'entrata' ? '+' : '-'}{formatCurrency(m.importo)}
                      </td>
                      <td className="py-2 px-2 text-muted-foreground">{formatDate(m.data)}</td>
                    </tr>
                  );
                })}
                {movimentiDettaglio.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-muted-foreground">
                      Nessun movimento trovato per {currentYear}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Tabella Riepilogo Annuale */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Riepilogo Annuale {currentYear}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-medium">{t('common.month')}</th>
                  <th className="text-right py-2 px-3 font-medium text-green-600">Entrate</th>
                  <th className="text-right py-2 px-3 font-medium text-red-600">Uscite</th>
                  <th className="text-right py-2 px-3 font-medium">{t('common.balance')}</th>
                  <th className="text-right py-2 px-3 font-medium text-blue-600">Cumulativo</th>
                </tr>
              </thead>
              <tbody>
                {chartDataWithCumulative.map((m, idx) => {
                  const isCurrentMonth = idx === currentMonth && currentYear === new Date().getFullYear();
                  return (
                    <tr 
                      key={m.name} 
                      className={`border-b ${isCurrentMonth ? 'bg-blue-50' : ''} ${idx < currentMonth && currentYear === new Date().getFullYear() ? 'opacity-60' : ''}`}
                    >
                      <td className="py-2 px-3 font-medium">
                        {m.name}
                        {isCurrentMonth && <Badge className="ml-2 text-xs" variant="outline">{t('common.current')}</Badge>}
                      </td>
                      <td className="text-right py-2 px-3 text-green-600">{formatCurrency(m.entrate)}</td>
                      <td className="text-right py-2 px-3 text-red-600">{formatCurrency(m.uscite)}</td>
                      <td className={`text-right py-2 px-3 font-semibold ${m.saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(m.saldo)}
                      </td>
                      <td className={`text-right py-2 px-3 font-semibold ${m.saldoCumulativo >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                        {formatCurrency(m.saldoCumulativo)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-muted font-bold">
                  <td className="py-2 px-3">TOTALE</td>
                  <td className="text-right py-2 px-3 text-green-600">
                    {formatCurrency(monthlyData.reduce((s, m) => s + m.entrate, 0))}
                  </td>
                  <td className="text-right py-2 px-3 text-red-600">
                    {formatCurrency(monthlyData.reduce((s, m) => s + m.uscite, 0))}
                  </td>
                  <td className={`text-right py-2 px-3 ${monthlyData.reduce((s, m) => s + m.saldo, 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(monthlyData.reduce((s, m) => s + m.saldo, 0))}
                  </td>
                  <td className="text-right py-2 px-3 text-blue-600">-</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
